import { useEffect, useMemo, useRef, useState } from 'react';
import { SITE_IMAGE_SLOTS } from '@/lib/siteImages';
import { fetchSiteSettings, updateSiteSettings, uploadImage } from '@/lib/api';
import { useCollection } from '@/hooks/useCollection';
import type { MediaItem } from '@/lib/types';

export default function ImageSlotsManager() {
  const [slots, setSlots] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerSlot, setPickerSlot] = useState<string | null>(null);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const fileTarget = useRef<string | null>(null);
  const { rows: media } = useCollection<MediaItem>('media_library', 'created_at', false);

  useEffect(() => {
    fetchSiteSettings()
      .then((s) => setSlots(s.image_slots ?? {}))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load website images'))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof SITE_IMAGE_SLOTS>();
    SITE_IMAGE_SLOTS.forEach((s) => {
      const arr = map.get(s.group) ?? [];
      arr.push(s);
      map.set(s.group, arr);
    });
    return Array.from(map.entries());
  }, []);

  const currentValue = (key: string, fallback: string) => {
    const v = slots[key];
    return v && v.trim() ? v : fallback;
  };

  const setSlot = (key: string, url: string) => setSlots((p) => ({ ...p, [key]: url }));

  const triggerUpload = (key: string) => {
    fileTarget.current = key;
    fileRef.current?.click();
  };

  const onFilePicked = async (file: File | undefined) => {
    const key = fileTarget.current;
    if (!file || !key) return;
    setUploadingKey(key);
    setError(null);
    try {
      const url = await uploadImage(file, 'media');
      setSlot(key, url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploadingKey(null);
      fileTarget.current = null;
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await updateSiteSettings({ image_slots: slots });
      setMessage('Website images updated. Refresh the site to see the changes.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save website images');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="bg-white rounded-lg border border-background-200 p-6">
        <p className="text-center text-foreground-400 py-6">
          <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading images...
        </p>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg border border-background-200 p-6">
      <div className="flex items-center gap-2 mb-1">
        <i className="ri-gallery-line text-primary-600" aria-hidden="true" />
        <h3 className="font-heading font-semibold text-base text-foreground-900">Website Images</h3>
      </div>
      <p className="text-sm text-foreground-500 mb-5">
        Replace any image on the public website. Upload a new photo or pick one from your media library.
      </p>

      {message && (
        <div className="mb-4 px-4 py-3 rounded-md bg-accent-100 text-accent-900 text-sm border border-accent-300">{message}</div>
      )}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">{error}</div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onFilePicked(e.target.files?.[0])}
      />

      <div className="space-y-6">
        {grouped.map(([group, items]) => (
          <div key={group}>
            <h4 className="text-xs font-label font-semibold uppercase tracking-widest text-foreground-500 mb-3">{group}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map((slot) => {
                const value = currentValue(slot.key, slot.defaultUrl);
                const isCustom = Boolean(slots[slot.key] && slots[slot.key].trim());
                return (
                  <div key={slot.key} className="rounded-lg border border-background-200 overflow-hidden bg-background-50">
                    <div className="h-36 w-full overflow-hidden">
                      <img src={value} alt={slot.label} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="p-3">
                      <p className="text-xs font-medium text-foreground-700 mb-2">{slot.label}</p>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => triggerUpload(slot.key)}
                          disabled={uploadingKey === slot.key}
                          className="text-[11px] font-label px-2.5 py-1.5 rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {uploadingKey === slot.key ? (
                            <><i className="ri-loader-4-line animate-spin mr-1" aria-hidden="true" />Uploading</>
                          ) : (
                            <><i className="ri-upload-2-line mr-1" aria-hidden="true" />Upload</>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPickerSlot(slot.key)}
                          className="text-[11px] font-label px-2.5 py-1.5 rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-folder-image-line mr-1" aria-hidden="true" />Library
                        </button>
                        {isCustom && (
                          <button
                            type="button"
                            onClick={() => setSlots((p) => {
                              const next = { ...p };
                              delete next[slot.key];
                              return next;
                            })}
                            className="text-[11px] font-label px-2.5 py-1.5 rounded-md text-accent-700 hover:bg-accent-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-restart-line mr-1" aria-hidden="true" />Reset
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-6">
        <button type="button" onClick={save} disabled={saving} className="btn-primary whitespace-nowrap cursor-pointer">
          {saving ? 'Saving...' : 'Save Website Images'}
        </button>
      </div>

      {pickerSlot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPickerSlot(null)} aria-hidden="true" />
          <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
              <h3 className="font-heading font-semibold text-lg text-foreground-900">Choose from Media Library</h3>
              <button
                type="button"
                onClick={() => setPickerSlot(null)}
                className="p-2 rounded-md hover:bg-background-100 cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {media.length === 0 ? (
                <p className="text-center text-foreground-400 py-10 text-sm">
                  Your media library is empty. Upload photos in the Media Library section first.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {media.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setSlot(pickerSlot, item.url);
                        setPickerSlot(null);
                      }}
                      className="group rounded-lg overflow-hidden border border-background-200 hover:border-primary-400 transition-colors cursor-pointer text-left"
                    >
                      <div className="h-28 w-full overflow-hidden">
                        <img src={item.url} alt={item.title || 'Library photo'} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform" />
                      </div>
                      <p className="text-[11px] text-foreground-600 px-2 py-1.5 line-clamp-1">{item.title || 'Untitled'}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}